import type { TFunction } from 'i18next';
import { ArrowUpRight, RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { AssetWithStats } from '@/common/asset';
import { ROLE } from '@/common/role';
import type { WithDeletedBy } from '@/common/soft-delete';
import { AssetIcon, InspectionIcon } from '@/components/entity-icons';
import { DeletedOnIcon } from '@/components/soft-delete/DeletedInfo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { BulkSelection } from '@/hooks/use-bulk-selection';
import { deletedPreviewHref } from '@/hooks/use-deleted-preview';
import { usePermission } from '@/hooks/use-permission';
import { cn, formatCurrencyWithCents, formatDateOnly } from '@/lib/utils';
import { getConditionRatingColorsMapping } from '@/lib/utils/asset';
import { AssetInactiveBadge, AssetOrganizationLabel } from './AssetOrganizationLabel';

export type AssetCompactListProps = {
	assets: AssetWithStats[];
	isLoading: boolean;
	/** Row to highlight and scroll to, e.g. the asset the user just came back from. */
	selectedAssetId?: string | null;
	/** When given, rows get checkboxes for bulk actions (owned by the page). */
	selection?: BulkSelection;
	/** Label each asset with its organization, when assets of several organizations are listed. */
	showOrganization?: boolean;
	/**
	 * Deleted-items view (GH #331): rows show when and by whom the asset was deleted and a Restore
	 * button instead of the stats, and open the asset as a read-only preview.
	 */
	onRestore?: (asset: AssetWithStats) => void;
};

/**
 * Assets of an inactive organization are read-only and the backend refuses to delete them,
 * so they are left out of bulk selection. The page builds its selection ids with this too.
 */
export const isBulkSelectable = (asset: AssetWithStats) => asset.organization?.enabled !== false;

// Same colours as DamageStatBox, one column per class on wide screens
const damageColumns = (t: TFunction) =>
	[
		{
			key: 'crit',
			label: t('assets.card.severity.crit'),
			short: t('assets.card.severityShort.crit'),
			className: 'text-red-600',
		},
		{
			key: 'high',
			label: t('assets.card.severity.high'),
			short: t('assets.card.severityShort.high'),
			className: 'text-orange-600 dark:text-yellow-600',
		},
		{
			key: 'med',
			label: t('assets.card.severity.med'),
			short: t('assets.card.severityShort.med'),
			className: 'text-yellow-600 dark:text-yellow-500',
		},
		{
			key: 'low',
			label: t('assets.card.severity.low'),
			short: t('assets.card.severityShort.low'),
			className: 'text-blue-600',
		},
	] as const;

const SKELETON_ROWS = 6;
// Upper bound of columns, used for full-width rows (loading, empty)
const MAX_COLUMNS = 13;

// Columns drop out as the viewport narrows: work packages first, then the
// inspection and the four damage columns (replaced by one summary), then the location.
// The list is 48 px narrower than the viewport, at most 1488 px (pageV1). Every column
// together needs about 1300 px, so the work packages wait for 2xl; the rest need about
// 1100 px, so the inspection and damage columns wait for xl (a 1232 px list).
const COL_LOCATION = 'hidden md:table-cell';
const COL_INSPECTION = 'hidden xl:table-cell';
const COL_DAMAGE = 'hidden xl:table-cell';
const COL_DAMAGE_SUMMARY = 'xl:hidden';
const COL_WORK_PACKAGE = 'hidden 2xl:table-cell';

/**
 * Compact alternative to the card grid: one row per asset with the same facts
 * (rating, ongoing/last inspection, damage counts, work package totals), so many
 * assets fit on one screen. Columns follow the same permissions as the cards.
 */
export function AssetCompactList({
	assets,
	isLoading,
	selectedAssetId,
	selection,
	showOrganization = false,
	onRestore,
}: AssetCompactListProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
	const DAMAGE_COLUMNS = useMemo(() => damageColumns(t), [t]);

	useEffect(() => {
		if (!selectedAssetId) return;
		rowRefs.current[selectedAssetId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [selectedAssetId]);
	const deletedView = !!onRestore;
	// A deleted asset's inspections, damages and work packages are deleted with it: no stats to show
	const canReadInspections = usePermission(ROLE.INSPECTIONS_READ) && !deletedView;
	const canReadDamages = usePermission(ROLE.DAMAGES_READ) && !deletedView;
	const canReadWorkPackages = usePermission(ROLE.WORK_PACKAGES_READ) && !deletedView;

	return (
		<div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						{selection && (
							<TableHead className="w-10">
								<Checkbox
									checked={selection.allSelected ? true : selection.someSelected ? 'indeterminate' : false}
									onCheckedChange={selection.toggleAll}
									aria-label={t('assets.list.selectAllAriaLabel')}
								/>
							</TableHead>
						)}
						<TableHead className="min-w-[220px] w-[32%]">{t('assets.list.columnAsset')}</TableHead>
						<TableHead className={COL_LOCATION}>{t('common.labels.location')}</TableHead>
						<TableHead className="text-center">{t('assets.list.columnRating')}</TableHead>
						{canReadInspections && <TableHead className={COL_INSPECTION}>{t('assets.list.lastInspection')}</TableHead>}
						{canReadDamages &&
							DAMAGE_COLUMNS.map((column) => (
								<TableHead
									key={column.key}
									className={cn('text-center', COL_DAMAGE)}
									title={t('assets.list.activeDamagesTitle', { label: column.label })}
								>
									{column.label}
								</TableHead>
							))}
						{canReadDamages && (
							<TableHead
								className={cn('text-center', COL_DAMAGE_SUMMARY)}
								title={t('assets.list.activeDamagesSummaryTitle')}
							>
								{t('assets.list.columnDamages')}
							</TableHead>
						)}
						{canReadWorkPackages && (
							<>
								<TableHead className={cn('text-right', COL_WORK_PACKAGE)}>{t('assets.card.pendingWP')}</TableHead>
								<TableHead className={cn('text-right', COL_WORK_PACKAGE)}>{t('assets.card.assignedWP')}</TableHead>
							</>
						)}
						<TableHead className="w-10" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{isLoading &&
						Array.from({ length: SKELETON_ROWS }).map((_, index) => (
							<TableRow
								key={`asset-row-skeleton-${
									// biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows have no identity
									index
								}`}
							>
								<TableCell colSpan={MAX_COLUMNS}>
									<Skeleton className="h-5 w-full" />
								</TableCell>
							</TableRow>
						))}

					{!isLoading &&
						assets.map((asset) => {
							const latestInspection = asset.latestInspection;
							const pendingCount = asset.financeStats?.Pending?.count || 0;
							const assignedCount = asset.financeStats?.Assigned?.count || 0;
							// The asset the user came back from gets a stripe on the left, so it stays
							// apart from rows that are only checked for a bulk action.
							const isCurrent = selectedAssetId === asset.id;
							const isChecked = !!selection?.isSelected(asset.id as string);
							return (
								<TableRow
									key={asset.id}
									ref={(el) => {
										rowRefs.current[asset.id || ''] = el;
									}}
									aria-current={isCurrent ? 'true' : undefined}
									className={cn(
										'cursor-pointer hover:bg-muted/50 transition-colors',
										isChecked && 'bg-primary/5 hover:bg-primary/10',
										isCurrent &&
											'bg-primary/10 hover:bg-primary/15 [&>td:first-child]:shadow-[inset_3px_0_0_var(--primary)]',
									)}
									onClick={() =>
										navigate(deletedView ? deletedPreviewHref(`/assets/${asset.id}`, 'asset') : `/assets/${asset.id}`)
									}
								>
									{selection && (
										<TableCell onClick={(e) => e.stopPropagation()}>
											{isBulkSelectable(asset) && (
												<Checkbox
													checked={isChecked}
													onCheckedChange={() => selection.toggle(asset.id as string)}
													aria-label={t('assets.list.selectAssetAriaLabel', {
														name: asset.name ?? t('common.entities.asset', { count: 1 }),
													})}
												/>
											)}
										</TableCell>
									)}
									<TableCell className="font-medium">
										<div className="min-w-0 max-w-[420px]">
											<div className="flex items-center gap-2 min-w-0">
												<AssetIcon
													className="size-4 shrink-0 text-muted-foreground"
													aria-label={t('assets.card.iconLabel')}
												/>
												<span className="truncate" title={asset.name}>
													{asset.name}
												</span>
												{asset.hasOngoingInspection && (
													<Badge className="shrink-0 text-[10px] px-1.5 py-0">{t('assets.card.ongoing')}</Badge>
												)}
												<AssetInactiveBadge asset={asset} className="text-[10px] py-0" />
												{deletedView && (
													<DeletedOnIcon
														deletedAt={asset.deletedAt}
														deletedBy={(asset as WithDeletedBy<AssetWithStats>).deletedBy}
													/>
												)}
											</div>
											{showOrganization && <AssetOrganizationLabel asset={asset} className="mt-0.5 pl-6" />}
										</div>
									</TableCell>
									<TableCell
										className={cn('text-muted-foreground max-w-[260px] truncate', COL_LOCATION)}
										title={asset.location ?? ''}
									>
										{asset.location || '—'}
									</TableCell>
									<TableCell className="text-center">
										<span
											className={cn(
												'text-xs font-bold px-2 py-0.5 rounded border',
												getConditionRatingColorsMapping(asset.conditionRating || 0),
											)}
										>
											{(asset.conditionRating ?? 0).toFixed(1)}
										</span>
									</TableCell>
									{canReadInspections && (
										<TableCell className={COL_INSPECTION}>
											{latestInspection ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="outline"
															size="xs"
															className="bg-white cursor-pointer"
															onClick={(e) => {
																// The row navigates to the asset; this goes one level deeper.
																e.stopPropagation();
																navigate(`/assets/${asset.id}/inspections/${latestInspection.id}`);
															}}
														>
															<InspectionIcon />
															{formatDateOnly(latestInspection.startDate)}
														</Button>
													</TooltipTrigger>
													<TooltipContent>Open the latest inspection: {latestInspection.title}</TooltipContent>
												</Tooltip>
											) : (
												<span className="text-muted-foreground">—</span>
											)}
										</TableCell>
									)}
									{canReadDamages &&
										DAMAGE_COLUMNS.map((column) => {
											const count = asset.damageStats?.[column.key] ?? 0;
											return (
												<TableCell
													key={column.key}
													className={cn(
														'text-center font-semibold tabular-nums',
														COL_DAMAGE,
														count > 0 ? column.className : 'text-muted-foreground/50',
													)}
												>
													{count}
												</TableCell>
											);
										})}
									{canReadDamages && (
										<TableCell className={cn('text-center whitespace-nowrap tabular-nums', COL_DAMAGE_SUMMARY)}>
											{DAMAGE_COLUMNS.map((column) => {
												const count = asset.damageStats?.[column.key] ?? 0;
												return (
													<span
														key={column.key}
														className={cn(
															'mx-1 text-xs font-semibold',
															count > 0 ? column.className : 'text-muted-foreground/50',
														)}
														title={`${column.label}: ${count}`}
													>
														{column.short}
														{count}
													</span>
												);
											})}
										</TableCell>
									)}
									{canReadWorkPackages && (
										<>
											<TableCell
												className={cn('text-right tabular-nums', COL_WORK_PACKAGE)}
												title={t('assets.card.pendingPackagesTitle', { count: pendingCount })}
											>
												{formatCurrencyWithCents(asset.financeStats?.Pending?.amount)}
											</TableCell>
											<TableCell
												className={cn('text-right tabular-nums', COL_WORK_PACKAGE)}
												title={t('assets.card.assignedPackagesTitle', { count: assignedCount })}
											>
												{formatCurrencyWithCents(asset.financeStats?.Assigned?.amount)}
											</TableCell>
										</>
									)}
									<TableCell>
										{deletedView ? (
											<Button
												variant="outline"
												size="sm"
												disabled={!isBulkSelectable(asset)}
												onClick={(event) => {
													// The row opens the asset; this only restores
													event.stopPropagation();
													onRestore?.(asset);
												}}
											>
												<RotateCcw />
												{t('common.actions.restore')}
											</Button>
										) : (
											<ArrowUpRight className="size-4 text-muted-foreground" />
										)}
									</TableCell>
								</TableRow>
							);
						})}

					{!isLoading && assets.length === 0 && (
						<TableRow>
							<TableCell colSpan={MAX_COLUMNS} className="text-center py-10 text-muted-foreground">
								{deletedView ? t('common.deleted.empty') : t('assets.list.emptyFiltered')}
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</div>
	);
}
